import { useState } from 'react'
import type { AvvAnnahme, AvvPartner, AvvVorschau } from '../data/supabaseAdapter'
import { AVV_FELDER, avvFeldBezeichnung, avvFehlerText, avvKanal, landName, sha256Hex } from '../lib/avv'
import { Button, Field, Input, Select, Sheet } from '../ui/kit'

export const AVV_LAENDER = ['AT', 'DE', 'CH', 'IT', 'GB', 'IE', 'OTHER'] as const
/** Bind the acceptance to exactly the displayed German bytes, never the translation. */
export async function gepruefteAvvAnnahme(v: AvvVorschau | null, partner: AvvPartner, checked: boolean): Promise<AvvAnnahme | null> {
  if (!checked || !v?.text || !v.sha256 || v.fehlend.length || AVV_FELDER.some(k => !partner[k].trim())) return null
  if (await sha256Hex(v.text) !== v.sha256) throw new Error('avv_hash_abweichend')
  return { kennung: v.kennung, sha256: v.sha256, partner: { ...partner }, kanal: avvKanal() }
}
export default function AvvSchritt({ preview, partner, countryCode, otherCountry, checked, pending, de, error,
  onPartner, onCountry, onOtherCountry, onChecked,
}: {
  preview: AvvVorschau | null; partner: AvvPartner; countryCode: string; otherCountry: string
  checked: boolean; pending: boolean; de: boolean; error: string | null
  onPartner: (p: AvvPartner) => void; onCountry: (code: string) => void; onOtherCountry: (name: string) => void
  onChecked: (value: boolean) => void
}) {
  const [reading, setReading] = useState(false)
  const [german, setGerman] = useState(false)
  if (!preview) return error ? <p role="status" className="text-sm text-muted">{error}</p> : null
  const complete = !!preview.text && !!preview.sha256 && preview.fehlend.length === 0 && !pending
  return <section className="border-t border-border pt-3 space-y-3" aria-label={de ? 'Auftragsverarbeitung' : 'Data processing agreement'}>
    <p className="font-medium">{preview.titel}</p>
    <p className="text-xs text-muted">{preview.kennung}</p>
    {(['firma', 'strasse', 'plz', 'ort'] as const).map(k => <Field key={k} label={avvFeldBezeichnung(k, de ? 'de' : 'en')}>
      <Input required value={partner[k]} maxLength={200} onChange={e => onPartner({ ...partner, [k]: e.target.value })} />
    </Field>)}
    <Field label={avvFeldBezeichnung('land', de ? 'de' : 'en')}>
      <Select required value={countryCode} onChange={e => onCountry(e.target.value)}>
        <option value="" disabled>{de ? 'Land wählen' : 'Select country'}</option>
        {AVV_LAENDER.map(c => <option key={c} value={c}>{c === 'OTHER' ? (de ? 'Anderes Land' : 'Other country') : landName(c)}</option>)}
      </Select>
    </Field>
    {countryCode === 'OTHER' && <Field label={de ? 'Land (vollständiger Name)' : 'Country (full name)'}>
      <Input required value={otherCountry} maxLength={200} onChange={e => onOtherCountry(e.target.value)} />
    </Field>}
    {preview.fehlend.length > 0 && <p className="text-sm text-muted">{avvFehlerText('avv_vertragspartner_unvollstaendig', preview.fehlend.join(','), de ? 'de' : 'en')}</p>}
    {error && <p role="alert" className="text-sm text-danger">{error}</p>}
    <Button type="button" disabled={!complete} onClick={() => { setGerman(de); setReading(true) }}>{de ? 'Vertrag lesen' : 'Read agreement'}</Button>
    {!de && <p className="text-xs text-muted">{preview.uebersetzung_en
      ? 'The German version is binding. The English translation is for information only.'
      : 'Only the German version is available and binding.'}</p>}
    <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" checked={checked} disabled={!complete} onChange={e => onChecked(e.target.checked)} />
      {de ? 'Ich nehme die deutsche Ausfertigung des Auftragsverarbeitungsvertrags einschließlich aller vier Anlagen an.' : 'I accept the German data processing agreement including all four annexes.'}
    </label>
    <Sheet open={reading} onClose={() => setReading(false)} title={preview.titel}>
      {!de && <>
        <p className="text-xs text-muted">{preview.uebersetzung_en ? 'The German version is binding.' : 'Only the German version is available and binding.'}</p>
        {preview.uebersetzung_en && <Button type="button" onClick={() => setGerman(!german)}>{german ? 'Show English translation' : 'Show binding German version'}</Button>}
      </>}
      <div className="whitespace-pre-wrap text-sm break-words">{!de && !german && preview.uebersetzung_en ? preview.uebersetzung_en : preview.text}</div>
    </Sheet>
  </section>
}
